import { Button, Flex, Heading, useToast } from "@chakra-ui/react";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { ScrollRestoration, useNavigate, useParams } from "react-router-dom";
import BasePage from "../common/BasePage";
import QuizForm from "../../components/forms/QuizForm";
import useGetQuizDetails from "../../hooks/api/quiz/useGetQuizDetails";
import { AddIcon } from "@chakra-ui/icons";
import NoItemsCard from "../../components/NoItemsCard";
import QuestionsTable from "../../components/tables/QuestionsTable";

type Params = {
  quizId: string;
};

export default function EditQuizPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { quizId } = useParams<Params>();
  const {
    data: quiz,
    isLoading,
    isError,
    refetch,
  } = useGetQuizDetails(quizId ?? "");

  const onSuccess = useCallback(() => {
    navigate("./../..");
    toast({
      title: t("edit_quiz_page.quiz_updated"),
      description: t("edit_quiz_page.quiz_updated_message"),
      status: "success",
      isClosable: true,
    });
  }, [navigate, t, toast]);

  const onError = useCallback(() => {
    toast({
      title: t("edit_quiz_page.quiz_error"),
      description: t("edit_quiz_page.quiz_error_message"),
      status: "error",
      isClosable: true,
    });
  }, [t, toast]);

  const goToCreateQuestion = useCallback(() => {
    navigate("./questions/create");
  }, [navigate]);

  return (
    <BasePage
      title={t("edit_quiz_page.edit_quiz")}
      isLoading={isLoading}
      hasError={isError}
      retry={refetch}
    >
      <ScrollRestoration />
      <Flex gap={16} flexDirection="column">
        <QuizForm
          quiz={quiz}
          lessonId={quiz?.lessonId}
          onSuccess={onSuccess}
          onError={onError}
        />
        <div>
          <Flex justifyContent="space-between">
            <Heading size="lg" as="h3" marginBottom={8}>
              {t("edit_quiz_page.questions")}
            </Heading>
            <Button
              bg="primary.DEFAULT"
              color="white"
              leftIcon={<AddIcon />}
              onClick={goToCreateQuestion}
            >
              {t("common.buttons.new")}
            </Button>
          </Flex>
          {quiz?.questions?.length ? (
            <QuestionsTable quizId={quiz.id!} />
          ) : (
            <NoItemsCard />
          )}
        </div>
      </Flex>
    </BasePage>
  );
}
